import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import { useMemo, useEffect } from "react";
import theme from "../../../theme";
import { cardIconSx } from "../../../theme/globalStyle/globalStyle";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "90%",
  maxWidth: 440,
  bgcolor: "#f7f7f7",
  borderRadius: "16px",
  boxShadow: "0 10px 40px rgba(0,0,0,0.14)",
  p: "24px",
  outline: "none",
};

const pill = {
  display: "inline-flex",
  alignItems: "center",
  gap: 8,
  px: 1.25,
  py: 0.75,
  borderRadius: "999px",
  bgcolor: "rgba(1,36,66,0.06)", // #012442 bem leve
  color: "#012442",
  fontWeight: 600,
  fontSize: 13,
};

const infoCard = {
  borderRadius: "14px",
  bgcolor: "#ffffff",
  border: "1px solid rgba(1,36,66,0.12)",
  p: 2,
};

const row = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: 2,
  py: 1.25,
};

const label = {
  color: theme.colors.textPrimary,
  fontSize: theme.typography.bodySize,
  fontWeight: theme.typography.weightSemibold,
  display: "flex",
  alignItems: "center",
  gap: 1,
};

const value = {
  color: theme.colors.textPrimary,
  fontSize: theme.typography.bodySize,
  fontWeight: theme.typography.weightBold,
  textAlign: "right",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  minWidth: 0,
};

const freightValue = {
  ...value,
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  gap: 1,
};

const newBadge = {
  px: 1,
  py: 0.25,
  borderRadius: "999px",
  background: theme.colors.highlightSoft,
  color: theme.colors.highlight,
  fontSize: theme.typography.captionSize,
  fontWeight: theme.typography.weightBold,
  lineHeight: theme.typography.captionLineHeight,
  flexShrink: 0,
};

const freightIconSx = {
  ...cardIconSx,
  width: 28,
  height: 28,
  borderRadius: `${theme.radius.small}px`,
  "& svg": {
    fontSize: 18,
  },
};

export default function FreightEditedSuccessModal({
  open,
  handleClose,
  onResult,
  dataMsg,
  title = "Tudo certo!",
}) {
  const data = useMemo(() => {
    const isSave = dataMsg?.isSave ?? false;
    const existing = dataMsg?.existing ?? false;
    const freightNumber = dataMsg?.freightNumber ?? "—";
    const msg = dataMsg?.msg ?? "Operação concluída com sucesso.";
    const origem = dataMsg?.firstDelivery?.name ?? "—";
    const origemCity = dataMsg?.firstDelivery?.city ?? "—";
    const origemUF = dataMsg?.firstDelivery?.uf ?? "—";
    const destino = dataMsg?.lastDelivery?.name ?? "—";
    const destinoCity = dataMsg?.lastDelivery?.city ?? "—";
    const destinoUF = dataMsg?.lastDelivery?.uf ?? "—";
    return { isSave, freightNumber, msg, origem, origemCity, origemUF, destino, destinoCity, destinoUF, existing };
  }, [dataMsg]);

  const handleConfirmClick = () => {
    onResult?.(true);
    // se você quiser fechar aqui, descomenta:
    // handleClose?.();
  };


  

  return (
    <Modal
      keepMounted
      open={open}
      onClose={handleClose}
      aria-labelledby="freight-edited-title"
      aria-describedby="freight-edited-description"
    >
      <Box sx={style}>
        {/* HEADER */}
        <Stack direction="row" alignItems="center" spacing={1}>
          <IconButton
            onClick={handleClose}
            sx={{
              p: 0,
              width: 40,
              height: 40,
              borderRadius: "999px",
              "&:hover": { bgcolor: "rgba(1,36,66,0.06)" },
            }}
            aria-label="Fechar"
          >
            <CloseIcon sx={{ color: "#012442" }} />
          </IconButton>

          <Typography
            id="freight-edited-title"
            variant="h6"
            sx={{
              fontWeight: 700,
              color: "#012442",
              flexGrow: 1,
              textAlign: "center",
              mr: "40px",
              letterSpacing: "-0.2px",
            }}
          >
            {title}
          </Typography>
        </Stack>

        <Divider sx={{ my: 2 }} />

        {/* BODY */}
        <Stack spacing={2}>
          {data.isSave ? (
            <Stack direction="row" alignItems="center" spacing={1}>
              <CheckCircleRoundedIcon sx={{ color: "#012442" }} />
              <Box sx={pill}>Frete Atualizado</Box>
            </Stack>
          ) : (
            <Stack direction="row" alignItems="center" spacing={1}>
              <CheckCircleRoundedIcon sx={{ color: "#012442" }} />
              <Box sx={pill}>Frete Salvo</Box>
            </Stack>
          )}

          <Typography
            id="freight-edited-description"
            sx={{
              color: "#012442",
              fontSize: 18,
              lineHeight: 1.5,
              fontWeight: 700,
            }}
          >
            {data.msg}
          </Typography>

          <Box sx={infoCard}>
            <Box sx={row}>
              <Typography sx={label}>
                <Box component="span" sx={freightIconSx}>
                  <LocalShippingOutlinedIcon fontSize="inherit" />
                </Box>
                Frete
              </Typography>
              <Box sx={freightValue} title={String(data.freightNumber)}>
                {data.existing && <Box sx={newBadge}>Novo número de frete</Box>}
                <Typography component="span" sx={value}>
                  {data.freightNumber}
                </Typography>
              </Box>
            </Box>

            <Divider sx={{ borderColor: "rgba(1,36,66,0.10)" }} />

            <Box sx={row}>
              <Typography sx={label}>⛟ Origem</Typography>
              <Typography sx={value} title={`${data.origem} - ${data.origemCity} - ${data.origemUF}`}>
                {data.origem ? `${data.origem} - ${data.origemCity} - ${data.origemUF}` : `${data.origemCity} - ${data.origemUF}`}
              </Typography>
            </Box>

            <Divider sx={{ borderColor: "rgba(1,36,66,0.10)" }} />

            <Box sx={row}>
              <Typography sx={label}>🏁 Destino</Typography>
              <Typography sx={value} title={`${data.destino} - ${data.destinoCity} - ${data.destinoUF}`}>
               {data.destino ? `${data.destino} - ${data.destinoCity} - ${data.destinoUF}` : `${data.destinoCity} - ${data.destinoUF}`}
              </Typography>
            </Box>
          </Box>
        </Stack>

        {/* FOOTER */}
        <Stack spacing={1.5} sx={{ mt: 3 }}>
          <Button
            fullWidth
            variant="contained"
            onClick={handleConfirmClick}
            sx={{
              backgroundColor: "#ff0000",
              borderRadius: "12px",
              py: 1.35,
              fontWeight: 700,
              textTransform: "none",
              fontSize: "15px",
              boxShadow: "none",
              "&:hover": {
                backgroundColor: "#cc0000",
                boxShadow: "none",
              },
            }}
          >
            Ok, entendi
          </Button>

          {/* opcional: ação secundária Airbnb */}
          {/* <Button
            onClick={handleClose}
            sx={{
              color: "#012442",
              textTransform: "none",
              fontWeight: 600,
              fontSize: "14px",
              textDecoration: "underline",
              "&:hover": { backgroundColor: "transparent" },
            }}
          >
            Fechar
          </Button> */}
        </Stack>
      </Box>
    </Modal>
  );
}
